import {
  QueryKeys,
  assertParameterIsNotUndefinedOrNull,
  introductionsApi,
  useAuthMutation,
  useAuthQuery,
} from '@sunrise-ui/api-client';
import type {
  IntroductionsByMeStates,
  MyIntroductionsStates,
} from '@sunrise-ui/api/network';
import {
  getFileFromUri,
  useAuthStore,
  useSnackbar,
} from '@sunrise-ui/primitives';
import { useQueryClient } from '@tanstack/react-query';

const { myIntroductionsKey, introductionsMadeByMeKey } =
  QueryKeys.introductions;

export const useGetMyIntroductions = ({
  userId,
  state,
}: {
  userId?: string;
  state: MyIntroductionsStates;
}) =>
  useAuthQuery({
    async queryFn() {
      assertParameterIsNotUndefinedOrNull(userId, 'userId');
      const response =
        await introductionsApi.listMyIntroductionsIntroductionsUserIdMyIntroductionsGet(
          {
            userId,
            state,
          },
        );
      return response.data;
    },
    queryKey: [myIntroductionsKey, state],
    enabled: Boolean(userId),
  });

export const useGetIntroductionsMadeByMe = ({
  userId,
  state,
}: {
  userId?: string;
  state: IntroductionsByMeStates;
}) =>
  useAuthQuery({
    async queryFn() {
      assertParameterIsNotUndefinedOrNull(userId, 'userId');
      const response =
        await introductionsApi.listIntroductionsMadeByMeIntroductionsUserIdMadeByMeGet(
          {
            userId,
            state,
          },
        );
      return response.data;
    },
    queryKey: [introductionsMadeByMeKey, state],
    enabled: Boolean(userId),
  });

type IntroducerInput = {
  requestorId: string;
  acceptorId: string;
  message: string;
};

export const useCreateIntro = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn({ requestorId, acceptorId, message }: IntroducerInput) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return introductionsApi.introduceUsersIntroductionsPost({
        introducedToInput: {
          introduced_person_1_id: requestorId,
          introduced_person_2_id: acceptorId,
          introducer_id: currentUserId,
          introducer_message: message,
        },
      });
    },
    async onSuccess() {
      await queryClient.invalidateQueries({
        queryKey: [introductionsMadeByMeKey, 'pending'],
      });
    },
    onError() {
      showSnackbar({
        text: 'Failed to create intro. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useDenyIntro = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn(id: string) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return introductionsApi.denyIntroductionIntroductionsDenyPost({
        denyIntroInput: { id, user_id: currentUserId },
      });
    },
    async onSuccess() {
      // TODO: this should be invalidated based on whether the intro was made by me or not
      await queryClient.invalidateQueries({ queryKey: [myIntroductionsKey] });
      await queryClient.invalidateQueries({
        queryKey: [introductionsMadeByMeKey],
      });
    },
    onError() {
      showSnackbar({
        text: 'Failed to deny intro. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useAcceptIntroAsRequestor = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn({ uri, introId }: { uri: string; introId: string }) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      const file = await getFileFromUri(uri, currentUserId);
      return introductionsApi.requestorAcceptIntroductionIntroductionsRequestorAcceptPost(
        {
          file,
          introId,
          userId: currentUserId,
        },
      );
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [myIntroductionsKey] });
    },
    onError() {
      showSnackbar({
        text: 'Failed to accept intro. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useAcceptIntroAsIntroducer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn({ requestorId, acceptorId, message }: IntroducerInput) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return introductionsApi.introducerAcceptIntroductionIntroductionsIntroducerAcceptPost(
        {
          introducerAcceptIntroInput: {
            introduced_user_1_id: requestorId,
            introduced_user_2_id: acceptorId,
            introducer_id: currentUserId,
            introducer_message: message,
          },
        },
      );
    },
    async onSuccess() {
      await queryClient.invalidateQueries({
        queryKey: [introductionsMadeByMeKey],
      });
    },
    onError() {
      showSnackbar({
        text: 'Failed to send. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useAcceptIntroAsAcceptor = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn(id: string) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return introductionsApi.acceptorAcceptIntroductionIntroductionsAcceptorAcceptPost(
        {
          acceptIntroInput: { id, user_id: currentUserId },
        },
      );
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [myIntroductionsKey] });
    },
    onError() {
      showSnackbar({
        text: 'Failed to accept intro. Please try again.',
        type: 'error',
      });
    },
  });
};
